import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

import StepperItem from './stepper-item';
import DateOfBirthPicker from './date-of-birth-picker';

const { UI, Order, Locations, Customer, MapsService } = Mobi2Go;
const base_class_name = 'm2g-customer-loyalty-club-form';
const baseClass = class_name =>
    `${base_class_name}${class_name ? `${class_name}` : ''}`;
const className = Mobi2Go.helpers.classNames;

export default forwardRefWithTranslation('loyalty-club')(
    class LoyaltyClubForm extends React.Component {
        static propTypes = {
            t: PropTypes.func.isRequired,
        };

        static steps = [
            'join',
            'favourite_location',
            'subscriber',
            'dob',
            'gender',
            'post_code',
            'complete',
        ];

        state = {
            step: 0,
            date_of_birth: '2000-01-01',
            dietary_preference: [],
            post_code: '',
            locations_by_region: {},
            favourite_location_id: null,
            is_logged_in: false,
        };

        componentDidMount() {
            Locations.onReady(() => {
                this.setState({
                    locations_by_region: Locations.getByRegion(),
                });
            });

            Order.onReady(this.updateLoggedInState);
            Customer.bind({
                logIn: this.updateLoggedInState,
                logOut: this.updateLoggedInState,
            });

            MapsService.bind('coordinatesAvailable', () => {
                if (this.state.favourite_location_id !== null) {
                    return;
                }
                const closest_location = Locations.getClosestToCustomer();
                if (closest_location) {
                    this.setState({
                        favourite_location_id: closest_location.id,
                    });
                }
            });
        }

        updateLoggedInState = () => {
            this.setState({
                step: 0,
                is_logged_in: Customer.isLoggedIn(),
            });
        };

        getStep = () => LoyaltyClubForm.steps[this.state.step];

        nextStep = () => {
            this.setStep(++this.state.step);
        };

        setStep(step) {
            if (step >= LoyaltyClubForm.steps.length) {
                return;
            }
            this.setState({ step });
        }

        setGender = gender => {
            this.setState({ gender });
            Customer.update({ gender });
        };

        setPostCode = post_code => {
            this.setState({ post_code });
            Customer.update({ post_code });
        };

        onJoinAcceptClick = () => {
            this.nextStep();
            Order.log('agreed to join loyalty club');
        };

        onJoinDeclineClick = () => {
            this.props.onCancel();
            Order.log('declined to join loyalty club');
        };

        onFavouriteLocationNextClick = () => {
            const { locations_by_region } = this.state;
            let { favourite_location_id } = this.state;

            if (
                !favourite_location_id &&
                _.values(locations_by_region).length
            ) {
                favourite_location_id = _.values(locations_by_region)[0][0].id;
            }
            if (favourite_location_id) {
                const location = Locations.getById(favourite_location_id);
                Customer.update({ favourite_location: location.get() });
            }

            this.nextStep();
        };

        onSubscriberAcceptClick = () => {
            Customer.update({ is_subscriber: true });
            this.nextStep();
        };

        onSubscriberDeclineClick = () => {
            Customer.update({ is_subscriber: false });
            this.nextStep();
        };

        onDateOfBirthNextClick = () => {
            Customer.update({ date_of_birth: this.state.date_of_birth });
            this.nextStep();
        };

        onGenderNextClick = () => {
            Customer.update({ gender: this.state.gender });
            this.nextStep();
        };

        onGenderDeclineClick = () => {
            Customer.update({ gender: null });
            this.nextStep();
            this.setState({ gender: null });
        };

        onPostCodeNextClick = () => {
            Customer.update({ post_code: this.state.post_code });
            this.nextStep();
        };

        onCompleteClick = () => {
            this.props.onComplete();
        };

        render() {
            const { t } = this.props;
            const base_class = 'm2g-customer-loyalty-club-signup-form';

            if (!Customer.isLoggedIn()) {
                return null;
            }

            return (
                <div className={baseClass()} data-step={this.getStep()}>
                    <div
                        className={baseClass('-stepper')}
                        data-step={this.state.step}
                    >
                        <StepperItem
                            step_index={0}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-description')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-description-html m2g-custom-html'
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: t('description_html'),
                                    }}
                                />
                            </div>

                            <div
                                className={className(
                                    baseClass('-stepper__item__actions'),
                                    baseClass('-description')
                                )}
                            >
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--primary',
                                        'm2g-button--fat'
                                    )}
                                    onClick={this.onJoinAcceptClick}
                                >
                                    {t('join_button_label')}
                                </button>
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__secondary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--bare'
                                    )}
                                    onClick={this.onJoinDeclineClick}
                                >
                                    {t('decline_button_label')}
                                </button>
                            </div>
                        </StepperItem>

                        <StepperItem
                            step_index={1}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-favourite-location')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-stepper__item__title'
                                    )}
                                >
                                    {t('favourite_store_question')}
                                </div>
                                <div
                                    className={className(
                                        baseClass(
                                            '-favourite-location__selector'
                                        ),
                                        'm2g-input-container m2g-form-element-group'
                                    )}
                                    data-icon-after="select"
                                >
                                    <select
                                        value={
                                            new String(
                                                this.state.favourite_location_id
                                            )
                                        }
                                        className={className('m2g-select')}
                                        onChange={e =>
                                            this.setState({
                                                favourite_location_id:
                                                    e.target.value,
                                            })
                                        }
                                    >
                                        {_.map(
                                            this.state.locations_by_region,
                                            (locations, region_name) => (
                                                <optgroup
                                                    label={region_name}
                                                    key={region_name}
                                                >
                                                    {locations.map(location => (
                                                        <option
                                                            value={location.id}
                                                            key={location.id}
                                                        >
                                                            {location.name}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )
                                        )}
                                    </select>
                                </div>
                            </div>

                            <div
                                className={className(
                                    baseClass('-stepper__item__actions')
                                )}
                            >
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--primary',
                                        'm2g-button--fat'
                                    )}
                                    onClick={this.onFavouriteLocationNextClick}
                                >
                                    {t('next_button_label')}
                                </button>
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__secondary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--bare'
                                    )}
                                    onClick={this.onFavouriteLocationNextClick}
                                >
                                    {t('i_dont_know_button_label')}
                                </button>
                            </div>
                        </StepperItem>

                        <StepperItem
                            step_index={2}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-subscriber')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-stepper__item__title'
                                    )}
                                >
                                    {t('customer:subscriber_question')}
                                </div>
                            </div>

                            <div
                                className={className(
                                    baseClass('-stepper__item__actions')
                                )}
                            >
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--primary',
                                        'm2g-button--fat'
                                    )}
                                    onClick={this.onSubscriberAcceptClick}
                                >
                                    {t('yes_button_label')}
                                </button>
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__secondary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--bare'
                                    )}
                                    onClick={this.onSubscriberDeclineClick}
                                >
                                    {t('no_button_label')}
                                </button>
                            </div>
                        </StepperItem>

                        <StepperItem
                            step_index={3}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-dob')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-stepper__item__title'
                                    )}
                                >
                                    {t('date_of_birth_question')}
                                </div>
                                <div
                                    className={className(
                                        baseClass('-dob-input-container')
                                    )}
                                >
                                    <DateOfBirthPicker
                                        baseClass={baseClass}
                                        onChange={date_of_birth =>
                                            this.setState({ date_of_birth })
                                        }
                                        date_of_birth={this.state.date_of_birth}
                                        onFocus={() => this.setStep(3)}
                                    />
                                </div>
                            </div>
                            <div
                                className={className(
                                    baseClass('-stepper__item__actions')
                                )}
                            >
                                <button
                                    onClick={this.onDateOfBirthNextClick}
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--primary',
                                        'm2g-button--fat'
                                    )}
                                >
                                    {t('next_button_label')}
                                </button>
                            </div>
                        </StepperItem>

                        <StepperItem
                            step_index={4}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-gender')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-stepper__item__title'
                                    )}
                                >
                                    {t('gender_question')}
                                </div>
                                <div
                                    className={className(
                                        baseClass('-input-options')
                                    )}
                                >
                                    <div
                                        className={className(
                                            baseClass('-input-options__item'),
                                            'm2g-touchable'
                                        )}
                                        data-checked={
                                            this.state.gender === 'male'
                                        }
                                        onClick={() =>
                                            this.setState({
                                                gender:
                                                    this.state.gender !== 'male'
                                                        ? 'male'
                                                        : null,
                                            })
                                        }
                                    >
                                        <div
                                            className={className(
                                                baseClass(
                                                    '-input-options__item__icon'
                                                ),
                                                'm2g-icon m2g-icon--checkbox m2g-icon--tick'
                                            )}
                                        />
                                        <div
                                            className={className(
                                                baseClass(
                                                    '-input-options__item__label'
                                                )
                                            )}
                                        >
                                            {t('gender_male')}
                                        </div>
                                    </div>

                                    <div
                                        className={className(
                                            baseClass('-input-options__item'),
                                            'm2g-touchable'
                                        )}
                                        data-checked={
                                            this.state.gender === 'female'
                                        }
                                        onClick={() =>
                                            this.setState({
                                                gender:
                                                    this.state.gender !==
                                                    'female'
                                                        ? 'female'
                                                        : null,
                                            })
                                        }
                                    >
                                        <div
                                            className={className(
                                                baseClass(
                                                    '-input-options__item__icon'
                                                ),
                                                'm2g-icon m2g-icon--checkbox m2g-icon--tick'
                                            )}
                                        />
                                        <div
                                            className={className(
                                                baseClass(
                                                    '-input-options__item__label'
                                                )
                                            )}
                                        >
                                            {t('gender_female')}
                                        </div>
                                    </div>

                                    <div
                                        className={className(
                                            baseClass('-input-options__item'),
                                            'm2g-touchable'
                                        )}
                                        data-checked={
                                            this.state.gender === 'other'
                                        }
                                        onClick={() =>
                                            this.setState({
                                                gender:
                                                    this.state.gender !==
                                                    'other'
                                                        ? 'other'
                                                        : null,
                                            })
                                        }
                                    >
                                        <div
                                            className={className(
                                                baseClass(
                                                    '-input-options__item__icon'
                                                ),
                                                'm2g-icon m2g-icon--checkbox m2g-icon--tick'
                                            )}
                                        />
                                        <div
                                            className={className(
                                                baseClass(
                                                    '-input-options__item__label'
                                                )
                                            )}
                                        >
                                            {t('gender_other')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={className(
                                    baseClass('-stepper__item__actions')
                                )}
                            >
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--primary',
                                        'm2g-button--fat'
                                    )}
                                    onClick={this.onGenderNextClick}
                                >
                                    {t('next_button_label')}
                                </button>
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__secondary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--bare'
                                    )}
                                    onClick={this.onGenderDeclineClick}
                                >
                                    {t('i_d_rather_not_say_label')}
                                </button>
                            </div>
                        </StepperItem>

                        <StepperItem
                            step_index={5}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-post-code')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-stepper__item__title'
                                    )}
                                >
                                    {t('post_code_question')}
                                </div>
                                <input
                                    className={className(
                                        baseClass('-post-code__input'),
                                        'm2g-input',
                                        'm2g-input--fat'
                                    )}
                                    placeholder="Enter your post code"
                                    type="text"
                                    value={this.state.post_code}
                                    ref={el => {
                                        this.post_code_input = el;
                                    }}
                                    onFocus={() => this.setStep(5)}
                                    onChange={e =>
                                        this.setState({
                                            post_code: e.target.value,
                                        })
                                    }
                                    onKeyDown={e =>
                                        e.keyCode === 13 &&
                                        this.post_code_input.blur()
                                    }
                                />
                            </div>
                            <div
                                className={className(
                                    baseClass('-stepper__item__actions')
                                )}
                            >
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--primary',
                                        'm2g-button--fat'
                                    )}
                                    onClick={this.onPostCodeNextClick}
                                >
                                    {t('next_button_label')}
                                </button>
                            </div>
                        </StepperItem>

                        <StepperItem
                            step_index={6}
                            current_step={this.state.step}
                            className={className(
                                baseClass('-stepper__item'),
                                baseClass('-complete')
                            )}
                        >
                            <div
                                className={baseClass('-stepper__item__content')}
                            >
                                <div
                                    className={baseClass(
                                        '-stepper__item__title'
                                    )}
                                >
                                    {t('welcome_label')}
                                </div>
                            </div>

                            <div
                                className={className(
                                    baseClass('-stepper__item__actions')
                                )}
                            >
                                <button
                                    className={className(
                                        baseClass(
                                            '-stepper__item__actions__primary-button'
                                        ),
                                        'm2g-button',
                                        'm2g-button--fat'
                                    )}
                                    onClick={this.onCompleteClick}
                                >
                                    {t('done_label')}
                                </button>
                            </div>
                        </StepperItem>
                    </div>
                </div>
            );
        }
    }
);
